/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { ReactNode } from 'react';
import appConfig from '../config/app';
import { getUrlWithToken } from './getMediaURLWithToken';
import { getFirebaseToken } from '../../infra/auth/firebase-auth';

export const LINK_REGEX =
  /https?:\/\/(www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\+.~#?&//=]*)/g;

export const LINK_REGEX_2 = /\b((?:https?|ftp):\/\/[^\s/$.?#].[^\s]*)|((?:www|ftp)\.[^\s/$.?#].[^\s]*\.[a-z]{2,})/gi;

/**
 * Configuration for text formatting rules
 */
type RegexRule = {
  regex: RegExp;
  render: (fullMatch: string, innerContent: ReactNode) => ReactNode;
  // When false, inner content will be treated as plain text (no further formatting)
  formatInner?: boolean;
};

/**
 * Information about a matched formatting pattern
 */
interface MatchInfo {
  rule: RegexRule;
  fullMatch: string;
  innerContent: string;
  startIndex: number;
  length: number;
}

const REGEX_MAP: RegexRule[] = [
  {
    // ```multiline code block```
    regex: /```([\s\S]+?)```/g,
    formatInner: false,
    render: (_, content) => (
      <pre
        style={{
          display: 'inline',
          fontFamily: 'monospace',
        }}
      >
        {content}
      </pre>
    ),
  },
  {
    regex: /(?<![a-zA-Z0-9.])~([^~\s][^~\n]*[^~\s]|[^~\s])~(?![a-zA-Z0-9.])/g,
    render: (_, content) => <del>{content}</del>, // Strikethrough
  },
  {
    regex: /(?<![a-zA-Z0-9])\*([^*\s][^*\n]*[^*\s]|[^*\s])\*(?![a-zA-Z0-9])/g,
    render: (_, content) => <b>{content}</b>, // Bold
  },
  {
    regex: /(?<![a-zA-Z0-9.])_([^_\s][^_\n]*[^_\s]|[^_\s])_(?![a-zA-Z0-9.])/g,
    render: (_, content) => <i>{content}</i>, // Italic
  },
  {
    regex: /(?<![a-zA-Z0-9.])`((?:(?!\n).)+?)`(?![a-zA-Z0-9.])/g,
    formatInner: false,
    render: (_, content) => <code>{content}</code>, // Inline code
  },
  {
    regex: /\b(?:(?:https?|ftp):\/\/[^\s/$.?#][^\s]*|(?:www|ftp)\.[^\s/$.?#][^\s]*\.[a-z]{2,})/gi,
    formatInner: false,
    render: (fullMatch) => {
      let url = fullMatch;
      if (!url.startsWith('https://') && !url.startsWith('http://')) {
        url = `https://${url}`;
      }
      if (url.startsWith(`https://${appConfig.quickRepliesCloudFrontUrl}`)) {
        return (
          <a
            css={css`
              cursor: pointer;
              color: blue;
              text-decoration: underline;
            `}
            target="_blank"
            onClick={async () => {
              try {
                const token = await getFirebaseToken();
                window.open(getUrlWithToken(url, token ?? ''), '_blank', 'noopener,noreferrer');
              } catch {
                window.open(getUrlWithToken(url, ''), '_blank', 'noopener,noreferrer');
              }
            }}
          >
            {fullMatch}
          </a>
        );
      }
      return (
        <a href={url} target="_blank" rel="noopener noreferrer">
          {fullMatch}
        </a>
      );
    },
  },
  {
    regex: /\n/g,
    render: () => <br />, // Line break
  },
];

/**
 * Finds the earliest match from all formatting rules in the given text
 * Uses a greedy approach: when multiple matches start at the same position,
 * selects the longest one to handle nested patterns correctly.
 */
const findEarliestFormattingMatch = (text: string): MatchInfo | null => {
  let earliestMatch: MatchInfo | null = null;

  for (const rule of REGEX_MAP) {
    // Create a fresh RegExp instance to avoid state pollution from lastIndex
    const regex = new RegExp(
      rule.regex.source,
      rule.regex.flags.includes('g') ? rule.regex.flags : rule.regex.flags + 'g'
    );
    regex.lastIndex = 0; // Reset the regex state

    const match = regex.exec(text);
    if (!match) continue;

    const matchStart = match.index;
    const fullMatchedText = match[0];
    const innerContent = match[1] ?? ''; // Capture group 1 (empty for line break rule)

    // Update earliest match if:
    // 1. No match found yet, OR
    // 2. This match starts earlier, OR
    // 3. Same position but this match is longer (greedy)
    if (
      earliestMatch === null ||
      matchStart < earliestMatch.startIndex ||
      (matchStart === earliestMatch.startIndex && fullMatchedText.length > earliestMatch.length)
    ) {
      earliestMatch = {
        rule,
        fullMatch: fullMatchedText,
        innerContent,
        startIndex: matchStart,
        length: fullMatchedText.length,
      };
    }
  }

  return earliestMatch;
};

/**
 * Converts plain text with WhatsApp-style formatting into JSX elements
 * Supports nested formatting (e.g., bold within italic)
 *
 * @param text - The text to format
 * @returns Array of React nodes with applied formatting
 */
export function formatTextSegmentIntoJSX(text: string = ''): React.ReactNode[] {
  const result: React.ReactNode[] = [];
  let currentPosition = 0;

  while (currentPosition < text.length) {
    // Look for formatting patterns in the remaining text
    const remainingText = text.slice(currentPosition);
    const matchInfo = findEarliestFormattingMatch(remainingText);

    // No more matches found - add remaining text as plain text
    if (!matchInfo) {
      result.push(remainingText);
      break;
    }

    // Add any plain text that comes before the match
    if (matchInfo.startIndex > 0) {
      const plainTextBefore = remainingText.slice(0, matchInfo.startIndex);
      result.push(...formatTextSegmentIntoJSX(plainTextBefore));
    }

    // Prepare inner content based on rule preference
    const inner: ReactNode =
      matchInfo.rule.formatInner === false ? matchInfo.innerContent : formatTextSegmentIntoJSX(matchInfo.innerContent);

    // Render the formatted match using the rule's render function
    result.push(matchInfo.rule.render(matchInfo.fullMatch, inner));

    // Move cursor past this match
    currentPosition += matchInfo.startIndex + matchInfo.length;
  }

  return result;
}

export const formatText = (text: string) => {
  const segments = text.split(/(<[^>]+>)/g);

  const normalizeAnalyticsEnabledLinks = (input: string) => {
    let output = input.replace(/\[\$\$\[([^\]]+)\]\$\$\]/g, '$1').replace(/\$\$\[([^\]]+)\]\$\$/g, '$1');
    return output;
  };

  return segments.map((segment) => {
    const normalized = normalizeAnalyticsEnabledLinks(segment);
    return normalized ? formatTextSegmentIntoJSX(normalized) : [];
  });
};

export const formatSearchText = (text: string, string: string) => {
  const words = text.split(' ');

  const result = words
    .map((word) => {
      if (word.includes(string) && !new RegExp(LINK_REGEX).test(word)) {
        return word.replace(string, `*${string}*`);
      } else {
        return word;
      }
    })
    .join(' ');

  return formatText(result);
};

export default formatText;
